import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { Observable, Subject, Subscription } from 'rxjs';
import { isEmptyString, isEmptyValue } from '@helpers';

import { EcertBaseBlockComponent } from '@building-blocks/ecertnl-base-block/ecertnl-base-block.component';
import { ApplicationUtilsService, InputGewijzigdService } from '@util-services';

import * as $ from 'jquery';
import { AutocompleteOptie } from './ecertnl-autocomplete.model';

@Component({
  selector: 'ecert-autocomplete',
  templateUrl: './ecertnl-autocomplete.component.html',
  styleUrls: ['./ecertnl-autocomplete.component.scss', '../../app.component.css'],
  standalone: false,
})
export class EcertAutocompleteComponent extends EcertBaseBlockComponent implements OnInit, OnDestroy, OnChanges {
  @Input() id: any;
  @Input() opties: AutocompleteOptie[];
  @Input() optionLabelProperty: any;
  @Input() optionValueProperty: any;
  @Input() optionHideProperty: any;
  @Input() volledigeBreedte = false;
  @Input() refObjectProperty: any;
  @Input() hideLabel = false;
  @Input() zoekOpCode = false;
  @Input() startSearchAt = 0;
  @Input() storeCompleteOption = false;
  // Voor sommige componenten(zoals de postcode-nl) willen we de spaties binnen en buiten de waarde negeren
  @Input() negeerSpatiesBijFilter = false;
  @Input() geenGeldigeWaardeMelding: string;

  @ViewChild('input') input: ElementRef;

  @Output() selectionChange = new EventEmitter();
  @Output() autocompleteInitialize = new EventEmitter();
  @Output() inputChange = new EventEmitter();
  @Output() autocompleteFocus = new EventEmitter();
  @Output() resetEvent = new EventEmitter<void>();

  formControl: UntypedFormControl = new UntypedFormControl();
  sub: Subject<string[]> = new Subject<string[]>();
  gefilterdeOptiesObservable: Observable<string[]>;
  gefilterdeOpties: any;
  gekozenOptie = null;
  filterValue = '';
  filterTimeout = null;
  searchAgain = true;
  isInitialized = false;
  geenGeldigeWaarde = false;

  private subscriptions: Subscription[] = [];

  constructor(
    appUtils: ApplicationUtilsService,
    private inputGewijzigdService: InputGewijzigdService,
    private changeDetector: ChangeDetectorRef
  ) {
    super(appUtils);
    this.ref_objectPostCheck = this.setInitValue;
  }

  ngOnInit() {
    this.gefilterdeOptiesObservable = this.sub.asObservable();
    this.setDisabledValue();
    this.setFilter();

    if (this.ref_object && !this.ref_object[this.refObjectProperty]) {
      this.reset();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    super.ngOnChanges(changes);
    if (changes.opties) {
      this.setOpties();
      this.setInitValue();
    }

    if (changes.disabled) {
      this.setDisabled();
    }
  }

  private setOpties(): void {
    this.gefilterdeOpties = this.opties;

    // om te voorkomen dat de autocomplete bij tweede actie na drie letters gevuld wordt,
    // vullen via getOpgeslagenLabel is alleen bedoeld voor orderregelniveau productnaam
    // waarbij de productnaam gevuld wordt via een zoekTerm (aantal opties is dan 1).
    if (
      !this.isInitialized ||
      (this.action_identifier === 'D.CLE_EXPORTEENHEDEN.PDT_ID' && !!this.opties && this.opties.length === 1) ||
      this.action_identifier === 'CLE_EXPORTEENHEDEN.PDT_ID_CZU'
    ) {
      this.getOpgeslagenLabel();
    }

    // Als verplicht en er is maar één optie, dan wordt de autocomplete ingevuld met deze optie.
    if (
      this.vulAutomatisch &&
      this.verplicht &&
      !!this.opties &&
      this.opties.length === 1 &&
      !!this.opties[0][this.optionValueProperty] &&
      !!this.ref_object &&
      !!this.refObjectProperty &&
      isEmptyValue(this.ref_object[this.refObjectProperty])
    ) {
      this.ref_object[this.refObjectProperty] = this.opties[0][this.optionValueProperty];
      this.formControl.setValue(this.opties[0]);

      this.setWaarde(this.opties[0]);
    }

    this.filter();

    if (!!this.opties && this.opties.length && !!this.gefilterdeOpties && !this.gefilterdeOpties.length) {
      this.setWaarde(null);
    }
  }

  private setDisabled(): void {
    if (this.disabled) {
      this.formControl.disable();
    } else {
      this.formControl.enable();
    }
  }

  private setDisabledValue() {
    if (!isEmptyValue(this.ref_object) && !isEmptyValue(this.ref_object.muteerbaar)) {
      if (this._disabled || !this.ref_object.muteerbaar || (this.ref_object.eedOpSlot && !this.wijzigbaar)) {
        this.formControl.disable();
      } else {
        this.formControl.enable();
      }
    }
  }

  setFilter() {
    this.subscriptions.push(
      this.formControl.valueChanges.subscribe((val) => {
        if (this.filterTimeout != null) {
          clearTimeout(this.filterTimeout);
        }

        if (val == null) {
          val = '';
        }
        if (val.length < this.startSearchAt) {
          this.searchAgain = true;
          this.opties = [];
        } else if (val.length >= this.startSearchAt && this.searchAgain) {
          this.searchAgain = false;
          this.inputChange.emit(val);
        }
        this.filterTimeout = setTimeout(() => {
          const gefilterdeOpties = this.filter();
          if (!this.isInitialized) {
            if (gefilterdeOpties != null && gefilterdeOpties.length > 0) {
              this.autocompleteInitialize.emit(this.gefilterdeOpties[0]);
            } else {
              this.autocompleteInitialize.emit(null);
              // this.formControl.setValue(this.ref_object[this.refObjectProperty]);
            }
          }
          this.isInitialized = true;
          this.geenGeldigeWaarde = this.isGeenGeldigeWaarde();
        }, 650);
      })
    );
  }

  filter(): string[] {
    if (
      !isEmptyValue(this.opties) &&
      !isEmptyValue(this.optionLabelProperty) &&
      !isEmptyValue(this.optionValueProperty) &&
      !isEmptyValue(this.input)
    ) {
      let inputWaarde = this.input.nativeElement.value.toLowerCase();
      this.gefilterdeOpties = this.opties.filter((optie) => {
        if (this.formControl.value == null) {
          return true;
        }
        let optieLabelPropWaarde = optie[this.optionLabelProperty]?.toLowerCase();
        let optieValuePropWaarde = optie[this.optionValueProperty]?.toLowerCase();
        if (this.negeerSpatiesBijFilter) {
          optieLabelPropWaarde = optieLabelPropWaarde?.replace(' ', '');
          optieValuePropWaarde = optieValuePropWaarde?.replace(' ', '');
          inputWaarde = inputWaarde.replace(' ', '');
        }
        if (this.zoekOpCode) {
          return optieLabelPropWaarde?.includes(inputWaarde) || optieValuePropWaarde?.includes(inputWaarde);
        } else {
          return optieLabelPropWaarde?.includes(inputWaarde);
        }
      });
    }

    this.sub.next(this.gefilterdeOpties);
    return this.gefilterdeOpties;
  }

  clearValueAfterFilter() {
    if (typeof this.formControl.value === 'string' || isEmptyString(this.input.nativeElement.value)) {
      this.setWaarde(null);
    }
  }

  onEnter() {
    // let eersteOptie = this.gefilterdeOpties[0];
    // this.formControl.setValue(eersteOptie);
    // this.setWaarde(eersteOptie);
  }

  setInitValue() {
    if (
      !isEmptyValue(this.optionLabelProperty) &&
      !isEmptyValue(this.optionValueProperty) &&
      !isEmptyValue(this.refObjectProperty) &&
      !isEmptyValue(this.ref_object) &&
      !isEmptyString(this.ref_object[this.refObjectProperty])
    ) {
      if (this.opties) {
        this.gefilterdeOpties = this.opties.filter((optie) => {
          if (typeof this.ref_object[this.refObjectProperty] === 'string') {
            return (
              optie[this.optionLabelProperty]?.toLowerCase() === this.ref_object[this.refObjectProperty].toLowerCase() ||
              optie[this.optionValueProperty]?.toLowerCase() === this.ref_object[this.refObjectProperty].toLowerCase()
            );
          } else {
            return (
              optie[this.optionLabelProperty]?.toLowerCase() ===
                this.ref_object[this.refObjectProperty][this.optionValueProperty].toLowerCase() ||
              optie[this.optionValueProperty]?.toLowerCase() ===
                this.ref_object[this.refObjectProperty][this.optionValueProperty].toLowerCase()
            );
          }
        });

        if (this.gefilterdeOpties.length) {
          this.formControl.setValue(this.gefilterdeOpties[0]);
          this.autocompleteInitialize.emit(this.gefilterdeOpties[0]);
        } else {
          this.autocompleteInitialize.emit(null);
          // this.formControl.setValue(this.ref_object[this.refObjectProperty]);
        }
      } else {
        this.formControl.setValue(this.ref_object[this.refObjectProperty]);
      }
    }
  }

  onBlur() {
    this.clearValueAfterFilter();

    // let eersteOptie = this.gefilterdeOpties[0];

    /*if (this.formControl.value === '' || typeof eersteOptie == 'undefined' || eersteOptie == null) {
        this.ref_object[this.refObjectProperty] = '';
        this.onAutocompleteChange.emit();
    } else if(this.gekozenOptie === '' || this.gekozenOptie == null){
        this.setWaarde(eersteOptie);
    }*/
  }

  isDisabled() {
    // De functie escapeSelector escapes elk character welke een speciale betekenis heeft in een CSS-selector.
    // Er wordt bijvoorbeeld een escape van een slash gedaan omdat anders de lengte-bepaling problemen geeft,
    // en zelfs de hele pagina niet goed toont.
    const query = $.escapeSelector('#' + this.id + ':disabled');
    return $(query).length > 0;
  }

  isWaardeLeeg() {
    return isEmptyString(this.formControl.value);
  }

  setWaarde(optie: any) {
    this.gekozenOptie = optie;

    if (isEmptyValue(this.optionValueProperty) || this.storeCompleteOption) {
      if (!isEmptyValue(optie)) {
        this.formControl.setValue(optie);
        this.ref_object[this.refObjectProperty] = optie;
      } else {
        this.formControl.setValue(this.defaultOptie);
        this.ref_object[this.refObjectProperty] = this.defaultOptie;
      }
    } else {
      this.formControl.setValue(optie);
      if (!isEmptyValue(optie)) {
        this.ref_object[this.refObjectProperty] = optie[this.optionValueProperty];
      } else {
        this.ref_object[this.refObjectProperty] = null;
      }
    }
    this.selectionChange.emit(this.gekozenOptie);
    this.changeDetector.detectChanges();
  }

  getOptieValue(optie: any) {
    if (optie != null) {
      if (this.optionLabelProperty === 'codeEnNaam') {
        // Label met zowel landcode als landnaam.
        // Dan worden drie spaties tussen landcode en landnaam gezet.
        const spaties = '\xa0\xa0\xa0';
        const label = optie[this.optionLabelProperty];
        if (label != null && label.indexOf('\n') > -1) {
          return this.getCode(optie).concat(spaties, this.getNaam(optie)).substring(0, label.indexOf('\n'));
        } else {
          return this.getCode(optie).concat(spaties, this.getNaam(optie));
        }
      } else {
        // Overige labels
        const label = optie[this.optionLabelProperty];
        if (label != null && label.indexOf('\n') > -1) {
          return optie[this.optionLabelProperty].substring(0, label.indexOf('\n'));
        } else {
          return optie[this.optionLabelProperty];
        }
      }
    }
    return '';
  }

  isSelected(optie) {
    if (isEmptyValue(this.optionValueProperty)) {
      return (this.ref_object[this.refObjectProperty] = optie);
    } else {
      return (this.ref_object[this.refObjectProperty] = optie[this.optionValueProperty]);
    }
  }

  getOpgeslagenLabel() {
    if (!this.ref_object || !this.refObjectProperty || !this.opties || !this.optionValueProperty) {
      return null;
    }
    const waarde = this.ref_object[this.refObjectProperty];
    if (isEmptyString(waarde)) {
      return null;
    }
    for (const optie of this.opties) {
      if (optie[this.optionValueProperty] === waarde) {
        this.formControl.setValue(optie);
        this.gefilterdeOpties = [optie];
        return (this.optionLabelProperty ? optie[this.optionLabelProperty] : null) || null;
      }
    }
    return null;
  }

  public getGekozenOptie() {
    return this.gekozenOptie;
  }

  public hasFocus() {
    /* 2019-11-13: Uitcommentarieren van this.reset als de gekozen optie leeg is.
     *
     * In Internet Explorer werd het zoek-veld automatisch leeg gemaakt als het enige tijd
     * duurt voordat de lijst met gevonden producten wordt getoond.
     * Nadat de lijst wordt getoond (en de gebruiker heeft nog geen product geselecteerd)
     * wordt het zoek-veld (onterecht) meteen of na een aantal seconden automatisch leeg
     * gemaakt. Dit gebeurt in Internet Explorer door een focus actie.
     * Na het uitcommentarieren van this.reset wordt het zoek-veld niet meer automatisch
     * leeg gemaakt als het enige tijd duurt voordat de lijst met producten wordt getoond.
     * Dit probleem deed zich alleen voor in Internet Explorer.
     */

    // if(this.getGekozenOptie() == null){
    //    this.reset();
    // }
    this.autocompleteFocus.emit();
  }

  reset() {
    this.gekozenOptie = null;
    this.formControl.setValue(null);

    if (!isEmptyValue(this.ref_object) && !isEmptyString(this.refObjectProperty)) {
      this.ref_object[this.refObjectProperty] = null;
    }
    this.resetEvent.emit();

    this.selectionChange.emit(this.gekozenOptie);
  }

  refresh() {
    if (!isEmptyValue(this.ref_object) && !isEmptyString(this.refObjectProperty) && this.ref_object[this.refObjectProperty] == null) {
      this.reset();
    }
  }

  get defaultOptie() {
    return undefined;
  }

  public clear() {
    this.reset();
  }

  hasErrors() {
    return false;

    // Onderstaande code zorgt ervoor dat er een melding komt bij het opslaan in CME/VDM
    // als verplichte autocomplete velden niet zijn gevuld. Kan eventueel gebruikt worden voor het kenteken.

    // let waarde = this.ref_object[this.refObjectProperty];
    // return this.verplicht && (waarde == null || waarde == '');
  }

  getCode(optie) {
    const label = optie[this.optionLabelProperty];
    if (isEmptyValue(label)) {
      return '';
    }
    return label.substr(0, label.indexOf(' '));
  }

  getNaam(optie) {
    const label = optie[this.optionLabelProperty];
    if (isEmptyValue(label)) {
      return '';
    }
    return label.substr(label.indexOf(' ') + 1);
  }

  getCodeClass(optie) {
    if (this.getCode(optie).length <= 2) {
      return 'col-code-kort';
    } else if (this.getCode(optie).length <= 5) {
      return 'col-code-middel';
    } else if (this.getCode(optie).length <= 8) {
      return 'col-code-lang';
    } else {
      return 'col';
    }
  }

  inputGewijzigd(value?: any, reset = false) {
    this.inputGewijzigdService.inputGewijzigd();
    if (reset) {
      this.reset();
    } else if (!isEmptyValue(value)) {
      this.setWaarde(value);
    }
    this.changeDetector.detectChanges();
  }

  cleanInput() {
    this.formControl.setValue(null);
  }

  private isGeenGeldigeWaarde() {
    return (
      !isEmptyValue(this.gefilterdeOpties) &&
      this.gefilterdeOpties.length == 0 &&
      !isEmptyValue(this.formControl) &&
      !isEmptyString(this.formControl.value) &&
      this.formControl.value.length >= this.startSearchAt &&
      typeof this.formControl.value === 'string' &&
      !this.isDisabled() &&
      this.appUtils.getPrompt(this.geenGeldigeWaardeMelding) !== this.geenGeldigeWaardeMelding &&
      this.appUtils.getPrompt(this.geenGeldigeWaardeMelding).toUpperCase() !== 'LEEG'
    );
  }

  ngOnDestroy() {
    this.subscriptions.forEach((s) => s.unsubscribe());
    this.sub.complete();
    this.gefilterdeOpties = null;
    this.ref_object = null;
    this.input = null;
    this.opties = null;
    this.formControl = null;
    // this.formControl.setValue(null);
  }
}
